/**
 * Endpoints #56-#66 — saved posts (SPEC `saved-posts`).
 *
 * The §5.2 half of the `/saved` page params goes through `viewFilterQuery`, the same serializer the
 * view screens and the global feed use, so a filter cannot be spelled one way here and another
 * there. Only the four saved-only narrowings and the keyset cursor are added.
 */

import type {
  PutSavedBody,
  SavedFeedQuery,
  SavedFeedResponse,
  SavedListDTO,
  SavedListsResponse,
  SavedStateDTO,
  SavedTagDTO,
  SavedTagsResponse,
} from '@lw/shared';
import { apiJson, apiVoid } from './client';
import { viewFilterQuery } from './views';

/* ------------------------------------------------------------------ lists */

/** #56 */
export function getSavedLists(signal?: AbortSignal): Promise<SavedListsResponse> {
  return apiJson<SavedListsResponse>('/saved/lists', { signal });
}

/** #57 */
export function createSavedList(name: string): Promise<SavedListDTO> {
  return apiJson<SavedListDTO>('/saved/lists', { method: 'POST', body: { name } });
}

/** #58 */
export function renameSavedList(id: number, name: string): Promise<SavedListDTO> {
  return apiJson<SavedListDTO>(`/saved/lists/${String(id)}`, { method: 'PATCH', body: { name } });
}

/** #59 — memberships cascade; the saves themselves survive. */
export function deleteSavedList(id: number): Promise<void> {
  return apiVoid(`/saved/lists/${String(id)}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------- tags */

/** #60 */
export function getSavedTags(signal?: AbortSignal): Promise<SavedTagsResponse> {
  return apiJson<SavedTagsResponse>('/saved/tags', { signal });
}

/** #67 — an empty tag from the sidebar; the colour is assigned server-side. */
export function createSavedTag(name: string): Promise<SavedTagDTO> {
  return apiJson<SavedTagDTO>('/saved/tags', { method: 'POST', body: { name } });
}

/** #61 — the colour is server-owned and deliberately not settable. */
export function renameSavedTag(id: number, name: string): Promise<SavedTagDTO> {
  return apiJson<SavedTagDTO>(`/saved/tags/${String(id)}`, { method: 'PATCH', body: { name } });
}

/** #62 — the join rows cascade; every post that carried the tag stays saved. */
export function deleteSavedTag(id: number): Promise<void> {
  return apiVoid(`/saved/tags/${String(id)}`, { method: 'DELETE' });
}

/* ------------------------------------------------------------ one post */

/** #63 — the save popup's prefill. */
export function getPostSavedState(postId: number, signal?: AbortSignal): Promise<SavedStateDTO> {
  return apiJson<SavedStateDTO>(`/posts/${String(postId)}/saved`, { signal });
}

/** #64 — the popup's one batch commit (replace-all). */
export function putPostSaved(postId: number, body: PutSavedBody): Promise<SavedStateDTO> {
  return apiJson<SavedStateDTO>(`/posts/${String(postId)}/saved`, { method: 'PUT', body });
}

/** #65 — idempotent. */
export function deletePostSaved(postId: number): Promise<void> {
  return apiVoid(`/posts/${String(postId)}/saved`, { method: 'DELETE' });
}

/* ------------------------------------------------------------------- feed */

/** #66 — the `/saved` screen. */
export function getSavedFeed(
  params: Partial<SavedFeedQuery> = {},
  signal?: AbortSignal,
): Promise<SavedFeedResponse> {
  return apiJson<SavedFeedResponse>('/saved/feed', {
    query: {
      ...viewFilterQuery(params),
      // The collection is not author-scoped, and everything on the screen is saved by definition.
      authorIds: undefined,
      saved: undefined,
      listIds: params.listIds,
      tagIds: params.tagIds,
      groupIds: params.groupIds,
      profileIds: params.profileIds,
      savedFrom: params.savedFrom,
      savedTo: params.savedTo,
      sort: params.sort,
      dir: params.dir,
      cursor: params.cursor,
      limit: params.limit,
    },
    signal,
  });
}
